import React from "react";
import Select from 'react-select';

const AnxietyForm = ({ formData, handleChange, anxietySymptomsMaster, isLoading }) => {
    const anxietySymptoms = [
        { field: "numbness_or_tingling", label: "Numbness or tingling" },
        { field: "feeling_hot", label: "Feeling hot" },
        { field: "wobbliness_in_legs", label: "Wobbliness in legs" },
        { field: "unable_to_relax", label: "Unable to relax" },
        { field: "fear_of_worst_happening", label: "Fear of worst happening" },
        { field: "dizzy_or_lightheaded", label: "Dizzy or lightheaded" },
        { field: "heart_pounding_racing", label: "Heart pounding / racing" },
        { field: "unsteady", label: "Unsteady" },
        { field: "terrified_or_afraid", label: "Terrified or afraid" },
        { field: "nervous", label: "Nervous" },
        { field: "feeling_of_choking", label: "Feeling of choking" },
        { field: "hands_trembling", label: "Hands trembling" },
        { field: "shaky_unsteady", label: "Shaky / unsteady" },
        { field: "fear_of_losing_control", label: "Fear of losing control" },
        { field: "difficulty_in_breathing", label: "Difficulty in breathing" },
        { field: "fear_of_dying", label: "Fear of dying" },
        { field: "scared", label: "Scared" },
        { field: "indigestion", label: "Indigestion" },
        { field: "faint_lightheaded", label: "Faint / lightheaded" },
        { field: "face_flushed", label: "Face flushed" },
        { field: "hot_cold_sweats", label: "Hot / cold sweats" }
    ];

    const scoreOptions = anxietySymptomsMaster.map(symptom => ({
        value: symptom.id,    // this will be sent to backend
        label: symptom.name,  // this is what the user sees
    }));

    const calculateScores = () => {
        let counts = [0, 0, 0, 0];
        let total = 0;

        anxietySymptoms.forEach((symptom) => {
            const val = parseInt(formData[symptom.field] || "0", 10);
            if (val >= 0 && val <= 3) {
                counts[val]++;
                total += val;
            }
        });

        const subtotals = [
            0, // 0 points × count
            1 * counts[1],
            2 * counts[2],
            3 * counts[3]
        ];

        return { subtotals, total };
    };

    const { subtotals, total } = calculateScores();

    const getInterpretation = (total) => {
        if (total <= 21) return "Low anxiety";
        if (total <= 35) return "Moderate anxiety";
        return "Potentially concerning levels of anxiety";
    };

    const interpretation = getInterpretation(total);

    return (
        <>
            {/* Guidelines Button */}
            <div className="row mb-4">
                <div className="col-md-4">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm w-100"
                        data-bs-toggle="modal"
                        data-bs-target="#baiGuidelines"
                    >
                        <i className="ri-information-line me-1"></i>
                        Guidelines, Scoring & Reference
                    </button>
                </div>
            </div>
            <div
                className="p-3"
                style={{
                    maxHeight: "60vh",
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#ccc transparent",
                }}
            >

                <div className="accordion" id="anxietyAccordion">
                    {/* Client Information */}
                    <div className="accordion-item mb-3 shadow-sm">
                        <h2 className="accordion-header">
                            <button
                                className="accordion-button"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#clientInfo"
                            >
                                Client Information
                            </button>
                        </h2>
                        <div id="clientInfo" className="accordion-collapse collapse show">
                            <div className="accordion-body mt-2">
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="client_name"
                                                placeholder="Client Name"
                                                value={formData.client_name || ""}
                                                onChange={(e) => handleChange({ value: e.target.value }, { name: e.target.name })}
                                                readOnly={!!formData.patient_id || !!formData.patientId}
                                            />
                                            <label>Client Name</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="diagnosis"
                                                placeholder="Diagnosis"
                                                value={formData.diagnosis || ""}
                                                onChange={(e) => handleChange({ value: e.target.value }, { name: e.target.name })}
                                            />
                                            <label>Diagnosis</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="attending_physician"
                                                placeholder="Attending Physician"
                                                value={formData.attending_physician || ""}
                                                onChange={(e) => handleChange({ value: e.target.value }, { name: e.target.name })}
                                            />
                                            <label>Attending Physician</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BAI Items Section */}
                    <div className="accordion-item mb-3 shadow-sm">
                        <h2 className="accordion-header">
                            <button
                                className="accordion-button collapsed fw-semibold"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#baiSection"
                            >
                                Beck Anxiety Inventory (BAI) - 21 Items
                            </button>
                        </h2>
                        <div id="baiSection" className="accordion-collapse collapse show">
                            <div className="accordion-body">
                                <p className="small text-muted text-center mb-4">
                                    Indicate how much you have been bothered by each symptom during the past month, including today.
                                </p>
                                {Array.from({ length: Math.ceil(anxietySymptoms.length / 2) }, (_, rowIndex) => {
                                    const i = rowIndex * 2;
                                    const item1 = anxietySymptoms[i];
                                    const item2 = anxietySymptoms[i + 1];

                                    return (
                                        <div key={rowIndex} className="row align-items-center mb-3">
                                            {/* First symptom + dropdown */}
                                            <div className="col-md-2 text-md-end fw-semibold text-secondary">
                                                {item1.label}
                                            </div>
                                            <div className="col-md-4">
                                                <Select
                                                    onChange={handleChange}
                                                    options={scoreOptions}
                                                    name={item1.field}
                                                    value={scoreOptions.find(option => option.value === parseInt(formData[item1.field])) || null}
                                                    isLoading={isLoading}
                                                    isDisabled={isLoading}
                                                />
                                            </div>

                                            {/* Second symptom + dropdown */}
                                            {item2 && (
                                                <>
                                                    <div className="col-md-2 text-md-end fw-semibold text-secondary">
                                                        {item2.label}
                                                    </div>
                                                    <div className="col-md-4">
                                                        <Select
                                                            onChange={handleChange}
                                                            options={scoreOptions}
                                                            name={item2.field}
                                                            value={scoreOptions.find(option => option.value === parseInt(formData[item2.field])) || null}
                                                            isLoading={isLoading}
                                                            isDisabled={isLoading}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                            {/* If odd number (last row), add empty space */}
                                            {!item2 && <div className="col-md-6"></div>}
                                        </div>
                                    );
                                })}

                                {/* Scoring Totals */}
                                <div className="mt-5 pt-4 border-top">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                        Score Calculation
                                    </small>
                                    <div className="row g-3">
                                        <div className="col-md-2">
                                            <div className="form-floating form-floating-outline mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={subtotals[0]}
                                                    placeholder="None"
                                                    readOnly={true}
                                                />
                                                <label>Not at all (0):</label>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-floating form-floating-outline mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={subtotals[1]}
                                                    placeholder="Mildly"
                                                    readOnly={true}
                                                />
                                                <label>Mildly (1):</label>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-floating form-floating-outline mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={subtotals[2]}
                                                    placeholder="Moderately"
                                                    readOnly={true}
                                                />
                                                <label>Moderately (2):</label>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-floating form-floating-outline mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={subtotals[3]}
                                                    placeholder="Severely"
                                                    readOnly={true}
                                                />
                                                <label>Severely (3):</label>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-floating form-floating-outline mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={total}
                                                    placeholder="Total"
                                                    readOnly={true}
                                                />
                                                <label>Total Score</label>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-floating form-floating-outline mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={interpretation}
                                                    placeholder="Interpretation"
                                                    readOnly={true}
                                                />
                                                <label>Interpretation</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Guidelines Modal */}
                <div className="modal fade" id="baiGuidelines" tabIndex="-1" aria-hidden="true">
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-body">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                    About
                                </small>
                                <p className="small">
                                    The Beck Anxiety Inventory (BAI) is a 21‑item self‑report scale designed to assess the severity of anxiety symptoms experienced over the past month, including today.
                                </p>

                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                    Items & Response
                                </small>
                                <p className="small">
                                    There are 21 items. Each item is rated on a 0–3 scale:
                                    0 = Not at all, 1 = Mildly (didn't bother me much), 2 = Moderately (was unpleasant at times), 3 = Severely (bothered me a lot).
                                    Scores for all items are summed to produce the total BAI score.
                                </p>

                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                    Reliability & Validity
                                </small>
                                <p className="small">
                                    The BAI has strong internal consistency (Cronbach's α ≈ 0.92) and acceptable test‑retest reliability (≈ 0.75 over one week). It correlates moderately with other anxiety measures and less with depression measures.
                                </p>

                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                    Scoring Interpretation
                                </small>
                                <p className="small">
                                    Interpret total scores as a general guide:
                                </p>
                                <ul className="small">
                                    <li><strong>0–21</strong> — Low anxiety</li>
                                    <li><strong>22–35</strong> — Moderate anxiety</li>
                                    <li><strong>36 and above</strong> — Potentially concerning levels of anxiety</li>
                                </ul>

                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                    Notes
                                </small>
                                <p className="small">
                                    Use scores alongside clinical judgment and other information. This form is intended as a screening/monitoring tool and not a standalone diagnostic instrument.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-primary" data-bs-dismiss="modal">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AnxietyForm;